import { iconCredit, iconMarkup } from '../icons.js';
import { setTooltip } from '../tooltips.js';
import { nimbleCreatorPolicy } from '../shell/nimble-credits.js';
import { interfaceScale, UI_SCALE } from '../shell/ui-scale.js';
import { mixer } from '../../audio/audio-mixer.js';
import { CHARACTER_DETAIL_CHOICES, GRASS_CHOICES, graphicsOptions, OVERHEAD_TEXT_CHOICES, SPELL_LIGHT_CHOICES, TORCH_LIGHT_CHOICES, type CharacterDetail, type GrassSetting, type TorchLights, type OverheadTextLevel, type SpellLights } from '../../rendering/graphics-options.js';
import { actionsIn, KEYMAP, keycaps, type KeyActionId } from '../../input/keymap.js';
import type { Setting, SettingsTab } from './settings-dialog.js';

export const CHARACTER_DETAIL_LABELS: Record<CharacterDetail, string> = { high: 'High', low: 'Low' };
export const GRASS_LABELS: Record<GrassSetting, string> = { full: 'Full', thin: 'Thin', off: 'Off' };
export const SPELL_LIGHT_LABELS: Record<SpellLights, string> = { all: 'Every caster', 8: '8 orbs', 4: '4 orbs', 2: '2 orbs', 1: '1 orb', 0: 'Glow only' };
export const TORCH_LIGHT_LABELS: Record<TorchLights, string> = { all: 'Every torch', 8: '8 torches', 4: '4 torches', 2: '2 torches', 1: '1 torch', 0: 'Glow only' };
export const OVERHEAD_TEXT_LABELS: Record<OverheadTextLevel, string> = { off: 'None', effects: 'Effects', all: 'Effects and damage' };

const percent = (value: number) => `${value}%`;

/** What the renderer draws: kept on this device and obeyed by the game and the scenario viewer alike. */
export function pictureTab({ detailChanged }: { detailChanged?: () => void } = {}): SettingsTab {
  return {
    id: 'picture', label: 'Picture', icon: 'watch', watch: refresh => graphicsOptions.onChange(refresh),
    settings: [
      { kind: 'toggle', id: 'picture-style', label: 'Picture style', get: () => graphicsOptions.pictureStyle, set: on => graphicsOptions.setPictureStyle(on) },
      { kind: 'toggle', id: 'shadows', label: 'Shadows', get: () => graphicsOptions.shadows, set: on => graphicsOptions.setShadows(on) },
      { kind: 'toggle', id: 'corpse-shadows', label: 'Corpse shadows', get: () => graphicsOptions.corpseShadows, set: on => graphicsOptions.setCorpseShadows(on), enabled: () => graphicsOptions.shadows },
      { kind: 'choice', id: 'character-detail', label: 'Character detail', choices: CHARACTER_DETAIL_CHOICES.map(value => ({ value, label: CHARACTER_DETAIL_LABELS[value] })),
        get: () => graphicsOptions.characterDetail,
        set: value => { const before = graphicsOptions.characterDetail; graphicsOptions.setCharacterDetail(value); if (graphicsOptions.characterDetail !== before) detailChanged?.(); } },
      { kind: 'choice', id: 'grass', label: 'Grass', choices: GRASS_CHOICES.map(value => ({ value, label: GRASS_LABELS[value] })),
        get: () => graphicsOptions.grass, set: value => graphicsOptions.setGrass(value) },
      { kind: 'choice', id: 'spell-lights', label: 'Spell lights', choices: SPELL_LIGHT_CHOICES.map(value => ({ value: String(value), label: SPELL_LIGHT_LABELS[value] })),
        get: () => String(graphicsOptions.spellLights), set: value => graphicsOptions.setSpellLights(value === 'all' ? 'all' : Number(value)) },
      { kind: 'choice', id: 'torch-lights', label: 'Torch lights', choices: TORCH_LIGHT_CHOICES.map(value => ({ value: String(value), label: TORCH_LIGHT_LABELS[value] })),
        get: () => String(graphicsOptions.torchLights), set: value => graphicsOptions.setTorchLights(value === 'all' ? 'all' : Number(value)) },
    ],
  };
}

/**
 * The interface around the world. `display` adds the game's fullscreen and cursor capture buttons (bound by `FullscreenControls`);
 * `combatLog` the log switch, where the HUD that owns the log is mounted.
 */
export function interfaceTab({ display = false, combatLog = false }: { display?: boolean; combatLog?: boolean } = {}): SettingsTab {
  const settings: Setting[] = [];
  if (display) settings.push({ kind: 'action', id: 'fullscreen', label: 'Fullscreen' }, { kind: 'action', id: 'capture-cursor', label: 'Capture cursor' });
  settings.push(
    { kind: 'range', id: 'ui-scale', label: 'Interface size', min: UI_SCALE.min, max: UI_SCALE.max, step: UI_SCALE.step, get: () => interfaceScale.percent, set: value => interfaceScale.set(value), format: percent },
    { kind: 'range', id: 'overhead-text', label: 'Overhead text', min: 0, max: OVERHEAD_TEXT_CHOICES.length - 1, step: 1,
      get: () => OVERHEAD_TEXT_CHOICES.indexOf(graphicsOptions.overheadText), set: value => graphicsOptions.setOverheadText(OVERHEAD_TEXT_CHOICES[value]),
      format: value => OVERHEAD_TEXT_LABELS[OVERHEAD_TEXT_CHOICES[value]] },
  );
  if (combatLog) settings.push({ kind: 'toggle', id: 'show-combat-log', label: 'Combat log', get: () => combatLogShown(), set: on => { setCombatLogShown(on); refreshCombatLog(); } });
  return { id: 'interface', label: 'Interface', icon: 'expand', settings, watch: refresh => graphicsOptions.onChange(refresh) };
}

/** The mixer's levels; the mute button beside the gear is the same switch. */
export function soundTab(): SettingsTab {
  const level = (id: string, label: string, key: 'master' | 'music' | 'sfx' | 'voices'): Setting =>
    ({ kind: 'range', id, label, min: 0, max: 100, step: 1, get: () => mixer[key], set: value => mixer.set({ [key]: value }), format: percent });
  return {
    id: 'sound', label: 'Sound', icon: 'soundOn', watch: refresh => mixer.onChange(refresh),
    settings: [
      { kind: 'toggle', id: 'volume-mute', label: 'Mute', get: () => mixer.muted, set: muted => mixer.set({ muted }) },
      level('volume-master', 'Master', 'master'), level('volume-music', 'Music', 'music'), level('volume-sfx', 'Effects', 'sfx'), level('volume-voices', 'Hero voices', 'voices'),
      { kind: 'range', id: 'voice-frequency', label: 'Hero chatter', min: 0, max: 200, step: 5, get: () => mixer.voiceFrequency, set: value => mixer.set({ voiceFrequency: value }), format: percent },
    ],
  };
}

/** One row of a key list: what it does, then its keys or mouse gesture as markup (`<kbd>` for keys). */
export type KeyRow = readonly [action: string, keys: string];
export interface KeyGroup { title?: string; rows: readonly KeyRow[] }
const k = (...keys: string[]) => keys.map(key => `<kbd>${key}</kbd>`).join(' ');

/** A keymap action's row: its label (or `label`) and keycaps, with `lead` (a mouse gesture) before them. */
const keyRow = (action: KeyActionId, lead = '', label = KEYMAP[action].label): KeyRow => [label, `${lead}${k(...keycaps(action))}`];

/**
 * The game's controls (`docs/controls.md`), also shown by the scenario viewer when it mounts the HUD. Keyboard rows come from the
 * keymap the handlers read, so a bound key cannot go unlisted; the mouse gestures are written here.
 */
export const GAME_KEYS: KeyGroup[] = [
  { title: 'Party', rows: [
    ['Select', 'Click · drag a box'], ['Add or remove', `${k('Shift')} click`], keyRow('selectHero'), keyRow('selectAll'),
    ['Reorder', 'Drag a medallion'], ['Centre on a hero', 'Double-click a medallion'],
  ] },
  { title: 'Orders', rows: [
    ['Move · attack', 'Click ground · enemy'], ['Queue', `${k('Shift')} order`], ['Turn the formation', `${k('Ctrl')} drag`], keyRow('radial', 'Right-click · '),
    ['Ping', `${k('Alt')} click · hold for the wheel`], keyRow('run'), keyRow('plates'), keyRow('pause'), keyRow('cancel'),
  ] },
  { title: 'Panels', rows: actionsIn('Panels').map(id => keyRow(id)) },
  { title: 'Camera', rows: [['Pan', `Screen edge · ${k(...keycaps('pan'))} · middle-drag`], ['Zoom', 'Wheel'], ['Release the cursor', k(...keycaps('cancel'))]] },
];

export function controlsTab(groups: readonly KeyGroup[]): SettingsTab {
  return {
    id: 'controls', label: 'Controls', icon: 'tactics',
    settings: groups.flatMap((group): Setting[] => [
      ...(group.title ? [{ kind: 'heading', label: group.title } as const] : []),
      { kind: 'content', render: () => `<dl class="control-guide">${group.rows.map(([action, keys]) => `<dt>${action}</dt><dd>${keys}</dd>`).join('')}</dl>` },
    ]),
  };
}

export function creditsTab(): SettingsTab {
  return { id: 'credits', label: 'Credits', icon: 'scroll', settings: [{ kind: 'content', render: () => `${nimbleCreatorPolicy}<p>${iconCredit}</p>` }] };
}

/** The mute button beside the gear: one press silences everything, as the Sound tab's switch does. */
export function bindMuteButton(button: HTMLElement) {
  const show = () => {
    const label = mixer.muted ? 'Unmute sound' : 'Mute sound';
    button.innerHTML = iconMarkup(mixer.muted ? 'soundOff' : 'soundOn');
    button.setAttribute('aria-pressed', String(mixer.muted));
    setTooltip(button, label, label);
  };
  button.addEventListener('click', () => mixer.set({ muted: !mixer.muted }));
  mixer.onChange(show); show();
}

const LOG_KEY = 'nimblez.showCombatLog';
let logShown: boolean | null = null;
/** On unless the player switched it off. */
function combatLogShown() {
  if (logShown === null) try { logShown = localStorage.getItem(LOG_KEY) !== 'false'; } catch { logShown = true; /* Storage may be unavailable. */ }
  return logShown;
}
function setCombatLogShown(on: boolean) {
  logShown = on;
  try { localStorage.setItem(LOG_KEY, String(on)); } catch { /* Keep the preference for this tab. */ }
}
/** Shows the combat log when the player wants it, the HUD is up and there is something in it. */
export function refreshCombatLog() {
  const log = document.getElementById('combat-log'), hud = document.getElementById('hud');
  if (log) log.hidden = !combatLogShown() || !hud || hud.hidden || !log.childElementCount;
}
